import { promptText } from '../../lib/prompt-dialog.js';
import { Icon } from '../ui/icons.js';
import { IconButton, TextField } from '../ui/controls.js';

/**
 * Editor for `settings.input.actions` (name → codes) or `settings.input.axes`
 * (name → { negative, positive, deadzone? }). Codes are typed comma-separated:
 * `KeyW`, `Space`, `Mouse0`, `MouseX`, `Gamepad_South`, `Gamepad0_LeftX`,
 * `Gamepad_LeftX+`, `Gamepad_RightTrigger` (see docs/systems/input-and-time.md).
 */

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => v !== null && typeof v === 'object' && !Array.isArray(v);

const toList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

const parseCodes = (text: string): string[] =>
  text
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);

export const CODE_HINT =
  'Comma-separated codes: keys (KeyW, Space, ShiftLeft, ArrowUp), mouse (Mouse0, MouseX, MouseY, MouseScroll), ' +
  'gamepad Gamepad_<Control> (any pad) or Gamepad0_<Control>: South/East/West/North, LeftShoulder, RightShoulder, ' +
  'LeftStick, RightStick, DpadUp/Down/Left/Right, Start, Select, Home, LeftX, LeftY, RightX, RightY, ' +
  'LeftTrigger, RightTrigger, half axes like LeftX+ / LeftY-.';

export function InputBindingsField({
  axes,
  value,
  onChange,
}: {
  /** Edit axes (negative/positive lists) instead of actions. */
  axes?: boolean;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const map: Obj = isObj(value) ? value : {};
  const names = Object.keys(map);

  const commit = (next: Obj) => onChange(Object.keys(next).length > 0 ? next : undefined);
  const setEntry = (name: string, entry: unknown) => commit({ ...map, [name]: entry });
  const remove = (name: string) => {
    const next = { ...map };
    delete next[name];
    commit(next);
  };
  const add = async () => {
    const name = (await promptText(axes ? 'New axis name (e.g. move_x)' : 'New action name (e.g. jump)'))?.trim();
    if (!name || name in map) return;
    setEntry(name, axes ? { negative: [], positive: [] } : []);
  };
  const rename = async (name: string) => {
    const next = (await promptText(`Rename ${axes ? 'axis' : 'action'} '${name}' (scripts read it by name)`, name))?.trim();
    if (!next || next === name || next in map) return;
    const out: Obj = {};
    for (const k of names) out[k === name ? next : k] = map[k];
    commit(out);
  };

  return (
    <div className="input-bindings">
      {names.length === 0 && (
        <span className="muted small">
          No {axes ? 'axes' : 'actions'} defined: scripts reading {axes ? 'axis(name)' : 'action_*(name)'} get{' '}
          {axes ? '0' : 'false'}.
        </span>
      )}
      {names.map((name) => {
        const entry = map[name];
        return (
          <div key={name} className="input-binding">
            <div className="input-binding-head">
              <button type="button" className="input-binding-name mono" title="Rename" onClick={() => void rename(name)}>
                {name}
              </button>
              <IconButton icon="trash" title={`Remove ${name}`} size={13} onClick={() => remove(name)} />
            </div>
            {axes ? (
              (['negative', 'positive'] as const).map((side) => {
                const axis = isObj(entry) ? entry : {};
                return (
                  <label key={side} className="input-binding-side" title={CODE_HINT}>
                    <span className="muted">{side === 'negative' ? '−' : '+'}</span>
                    <TextField
                      mono
                      value={toList(axis[side]).join(', ')}
                      placeholder={side === 'negative' ? 'KeyA, Gamepad_DpadLeft' : 'KeyD, Gamepad_LeftX'}
                      onChange={(text) => setEntry(name, { ...axis, [side]: parseCodes(text) })}
                    />
                  </label>
                );
              })
            ) : (
              <label className="input-binding-side" title={CODE_HINT}>
                <TextField
                  mono
                  value={toList(entry).join(', ')}
                  placeholder="Space, Gamepad_South"
                  onChange={(text) => setEntry(name, parseCodes(text))}
                />
              </label>
            )}
          </div>
        );
      })}
      <button type="button" className="ghost small" onClick={() => void add()}>
        <Icon name="plus" size={12} /> Add {axes ? 'axis' : 'action'}
      </button>
    </div>
  );
}
